import { Button, Grid, TextField, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { useState } from 'react';
import axios from '../../../_utils/axios';
import { getLineageMetaTitle } from '../../../_utils/utils';
import { getData } from '../../../redux/slices/dataTableSlice';
import PolarisIcon from '../PolarisIcon';
import LabelValue from '../LabelValue';
import Modal from '@/components/_common/Modal/Modal';
import OverflowMenuItem from '../OverflowMenu/OverflowMenuItem';
import TableStatus from '../Table/TableStatus';
import { useToast } from '../Toast';
import { OrderJobTask } from '@/redux/slices/slice_types';
import { selectCanWriteTasks } from '@/redux/selectors/authSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

interface TaskResolveProps {
  task: OrderJobTask;
  onResolutionSubmitted?: () => void;
  button?: boolean;
}

const TaskResolve = ({ task, onResolutionSubmitted, button = false }: TaskResolveProps) => {
  const dispatch = useAppDispatch();
  const canWriteTasks = useAppSelector(selectCanWriteTasks);
  const [open, setOpen] = useState(false);
  const [resolution, setResolution] = useState('');
  const [hasError, setHasError] = useState(false);
  const { error, success } = useToast();
  if (!canWriteTasks) return null;

  const handleModalOpen = (e: React.MouseEvent<HTMLButtonElement | HTMLLIElement>) => {
    e.stopPropagation();
    setOpen(true);
  };

  const handleModalClose = () => {
    setResolution('');
    setOpen(false);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (hasError) {
      setHasError(false);
    }
    setResolution(e.target.value);
  };

  const submitResolution = () => {
    if (!resolution.length) {
      setHasError(true);
      error('Please enter a resolution');
    } else {
      axios
        .post(`/core/tasks/${task.id}/resolution/`, { notes: resolution })
        .then((res) => {
          if (!res.data.success) {
            throw new Error();
          } else {
            success('Resolution submitted');
            setOpen(false);
            dispatch(getData());
            if (onResolutionSubmitted) onResolutionSubmitted();
          }
        })
        .catch(() => error('There was a problem resolving the task.'))
        .then(() => setResolution(''));
    }
  };

  return (
    <div>
      {button ? (
        <Button
          data-testid="mark-resolved-btn"
          color="primary"
          size="small"
          variant="text"
          startIcon={<PolarisIcon icon="check_double" height={16} />}
          onClick={handleModalOpen}
        >
          Mark as Resolved
        </Button>
      ) : (
        <OverflowMenuItem key="history" onClick={handleModalOpen}>
          <PolarisIcon icon="check_double" height={18} /> Mark as Resolved
        </OverflowMenuItem>
      )}
      <StyledModal
        loading={false}
        open={open}
        onClose={handleModalClose}
        title="Mark as Resolved"
        icon={<PolarisIcon icon="check_double" height={18} />}
      >
        <Grid data-testid="mark-resolved-modal-content" container columnSpacing={6} rowSpacing={2} sx={{ mb: 4 }}>
          <LabelValue label="Task ID" color="primary">
            {task.hid}
          </LabelValue>
          <LabelValue label="Status" color="primary">
            <TableStatus status={task.status} labelOverride={task.pretty_name} />
          </LabelValue>
          <LabelValue label="Title" color="primary">
            <Typography>
              <strong>{task.job?.title?.lineage_meta?.series_name || task.job?.title?.alternate_name}</strong>
            </Typography>
            <Typography variant="caption">{getLineageMetaTitle(task.job?.title)}</Typography>
          </LabelValue>
        </Grid>

        <TextField
          fullWidth
          id="resolution"
          label="Resolution Notes"
          multiline
          minRows={4}
          maxRows={8}
          value={resolution}
          error={hasError}
          onChange={handleInput}
          inputProps={{
            'data-testid': 'resolution-notes-input'
          }}
        />

        <Grid container justifyContent="flex-end" alignItems="center" sx={{ mt: 4 }}>
          <Button data-testid="cancel-resolve-btn" variant="text" color="default" onClick={handleModalClose} sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button data-testid="confirm-resolve-btn" color="primary" variant="contained" onClick={submitResolution}>
            Resolve
          </Button>
        </Grid>
      </StyledModal>
    </div>
  );
};

export default TaskResolve;

const StyledModal = styled(Modal)`
  .MuiPaper-root {
    max-width: 450px;

    & label {
      margin-bottom: ${(props) => props.theme.spacing(1)};
    }
  }
`;
